import { useState, useEffect } from 'react'
import { PageHeader } from '@/components/common/page-header'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { userProfileService } from '@/services/user-profile-service'

interface EmailPrefs {
  emailOnEstimateSubmitted: boolean
  emailOnEstimateApproved: boolean
  emailOnEstimateRejected: boolean
  emailOnProposalSigned: boolean
  emailOnProposalViewed: boolean
  emailOnProposalExpiring: boolean
}

const PREF_CONFIGS = [
  {
    key: 'emailOnEstimateSubmitted' as const,
    label: 'Estimate submitted for approval',
    description: 'Get notified when an estimator submits an estimate for your review',
    roles: ['MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
  {
    key: 'emailOnEstimateApproved' as const,
    label: 'Estimate approved',
    description: 'Get notified when your estimate is approved by a manager',
    roles: ['ESTIMATOR', 'MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
  {
    key: 'emailOnEstimateRejected' as const,
    label: 'Estimate rejected',
    description: 'Get notified when your estimate is rejected with feedback',
    roles: ['ESTIMATOR', 'MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
  {
    key: 'emailOnProposalSigned' as const,
    label: 'Proposal signed by client',
    description: 'Get notified when a client signs a proposal',
    roles: ['ESTIMATOR', 'MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
  {
    key: 'emailOnProposalViewed' as const,
    label: 'Proposal viewed by client',
    description: 'Get notified when a client first opens your proposal — great for timely follow-ups',
    roles: ['ESTIMATOR', 'MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
  {
    key: 'emailOnProposalExpiring' as const,
    label: 'Proposal expiring soon',
    description: 'Get a digest of proposals that are about to expire',
    roles: ['ESTIMATOR', 'MANAGER', 'ADMIN', 'SUPERADMIN'],
  },
]

export function UserSettingsPage() {
  const { user } = useAuthStore()
  const [prefs, setPrefs] = useState<EmailPrefs>({
    emailOnEstimateSubmitted: true,
    emailOnEstimateApproved: true,
    emailOnEstimateRejected: true,
    emailOnProposalSigned: true,
    emailOnProposalViewed: true,
    emailOnProposalExpiring: true,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    loadPrefs()
  }, [])

  const loadPrefs = async () => {
    try {
      const profile = await userProfileService.getMe()
      setPrefs({
        emailOnEstimateSubmitted: profile.emailOnEstimateSubmitted ?? true,
        emailOnEstimateApproved: profile.emailOnEstimateApproved ?? true,
        emailOnEstimateRejected: profile.emailOnEstimateRejected ?? true,
        emailOnProposalSigned: profile.emailOnProposalSigned ?? true,
        emailOnProposalViewed: profile.emailOnProposalViewed ?? true,
        emailOnProposalExpiring: profile.emailOnProposalExpiring ?? true,
      })
    } catch (err) {
      console.error('Failed to load preferences:', err)
    } finally {
      setLoading(false)
    }
  }

  const togglePref = async (key: keyof EmailPrefs) => {
    const newValue = !prefs[key]
    setPrefs(prev => ({ ...prev, [key]: newValue }))
    setSaving(key)
    setMessage(null)

    try {
      await userProfileService.updateEmailPreferences({ [key]: newValue })
      setMessage({ type: 'success', text: 'Preference saved' })
      setTimeout(() => setMessage(null), 2000)
    } catch (err) {
      // Revert on failure
      setPrefs(prev => ({ ...prev, [key]: !newValue }))
      setMessage({ type: 'error', text: 'Failed to save preference' })
    } finally {
      setSaving(null)
    }
  }

  const visiblePrefs = PREF_CONFIGS.filter(p => user?.role && p.roles.includes(user.role))

  if (loading) {
    return (
      <PageLoader />
    )
  }

  return (
    <div className="us-page">
      <PageHeader title="User Settings" />

      <div className="us-section">
        <div className="us-section-header">
          <div className="us-section-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div>
            <h2 className="us-section-title">Email Notifications</h2>
            <p className="us-section-desc">Choose which emails you'd like to receive. These are separate from in-app notifications.</p>
          </div>
        </div>

        {message && (
          <div className={`us-message ${message.type === 'success' ? 'us-message-success' : 'us-message-error'}`}>
            {message.text}
          </div>
        )}

        <div className="us-prefs-list">
          {visiblePrefs.map(config => (
            <div key={config.key} className="us-pref-row">
              <div className="us-pref-info">
                <span className="us-pref-label">{config.label}</span>
                <span className="us-pref-desc">{config.description}</span>
              </div>
              <button
                className={`us-toggle ${prefs[config.key] ? 'us-toggle-on' : ''}`}
                onClick={() => togglePref(config.key)}
                disabled={saving === config.key}
                aria-label={`Toggle ${config.label}`}
              >
                <span className="us-toggle-thumb" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
